import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useMunicipalStore } from "../../store/municipalStore";
import MetricCard from "../shared/MetricCard";
import StatusBadge from "../shared/StatusBadge";
import { Header, Container } from "../shared/Layout";
import Button from "../shared/Button";
import { CheckCircle2, Clock, AlertCircle, BarChart3 } from "lucide-react";

export default function PresidenteDashboard({
  presidentialId,
  currentTab,
  onTabChange = () => {}
}) {

  const requests = useMunicipalStore(state => state.requests);
  const [selectedRequest, setSelectedRequest] = useState(null);

  const myRequests = useMemo(() => {
    return requests.filter(r => r.presidentialId === presidentialId);
  }, [requests, presidentialId]);

  const stats = useMemo(() => {
    const byState = {};

    myRequests.forEach(r => {
      byState[r.estado] = (byState[r.estado] || 0) + 1;
    });

    return {
      total: myRequests.length,
      resueltas: byState["resuelto"] || 0,
      enProceso: byState["en_proceso"] || 0,
      pendientes:
        (byState["enviado"] || 0) +
        (byState["clasificado"] || 0)
    };

  }, [myRequests]);

  const timelineStates = [
    { key: "enviado", label: "Solicitud enviada" },
    { key: "clasificado", label: "Clasificada por alcaldía" },
    { key: "asignado", label: "Asignada a área" },
    { key: "en_proceso", label: "En proceso" },
    { key: "resuelto", label: "Problema resuelto" }
  ];

  const getCurrentIndex = (estado) => {
    return timelineStates.findIndex(s => s.key === estado);
  };

  return (
    <Container>

      <Header
        title="Tu Panel de Control"
        subtitle="Seguimiento simple de tus solicitudes"
        icon={BarChart3}
      />

      {/* MÉTRICAS */}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">

        <MetricCard
          title="Total de Solicitudes"
          value={stats.total}
          icon={AlertCircle}
          color="blue"
        />

        <MetricCard
          title="En Proceso"
          value={stats.enProceso}
          icon={Clock}
          color="orange"
        />

        <MetricCard
          title="Resueltas"
          value={stats.resueltas}
          icon={CheckCircle2}
          color="green"
        />

        <MetricCard
          title="Pendientes"
          value={stats.pendientes}
          icon={AlertCircle}
          color="purple"
        />

      </div>

      {/* SOLICITUDES */}

      <div className="mb-8">

        <h2 className="text-2xl font-bold text-gray-900 mb-6">
          Tus Solicitudes
        </h2>

        {myRequests.length === 0 ? (

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-gradient-to-br from-blue-50 to-purple-50 border-2 border-dashed border-blue-200 rounded-2xl p-12 text-center"
          >

            <AlertCircle
              className="mx-auto mb-4 text-blue-500"
              size={48}
            />

            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              No tienes solicitudes
            </h3>

            <p className="text-gray-600 mb-6">
              Crea tu primera solicitud para reportar un problema en tu barrio.
            </p>

            <Button
              variant="primary"
              size="lg"
              onClick={() => onTabChange("nueva-solicitud")}
            >
              ➕ Nueva Solicitud
            </Button>

          </motion.div>

        ) : (

          <div className="space-y-3">

            {myRequests.map((request, idx) => (

              <motion.div
                key={request.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                onClick={() => setSelectedRequest(request)}
                className="bg-white border border-gray-200 rounded-2xl p-4 hover:shadow-lg transition-all cursor-pointer hover:scale-[1.01]"
              >

                <div className="flex items-start justify-between gap-4">

                  <div className="flex-1">

                    <h3 className="font-semibold text-gray-900">
                      {request.tipo}
                    </h3>

                    <p className="text-sm text-gray-600 mt-1">
                      {request.barrio} • {request.descripcion.substring(0, 50)}...
                    </p>

                  </div>

                  <div className="flex items-center gap-3">
                    <StatusBadge estado={request.estado} />
                  </div>

                </div>

              </motion.div>

            ))}

          </div>

        )}

      </div>

      {/* MODAL */}

      <AnimatePresence>

        {selectedRequest && (

          <motion.div
            className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedRequest(null)}
          >

            <motion.div
              className="bg-white rounded-3xl shadow-2xl w-full max-w-xl p-6"
              initial={{ scale: 0.9, y: 40 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 40 }}
              transition={{ type: "spring", stiffness: 260, damping: 20 }}
              onClick={(e) => e.stopPropagation()}
            >

              {/* HEADER */}

              <div className="flex justify-between items-start mb-6">

                <div>

                  <h2 className="text-xl font-bold text-gray-900">
                    {selectedRequest.tipo}
                  </h2>

                  <p className="text-sm text-gray-500">
                    Detalle de la solicitud
                  </p>

                </div>

                <button
                  onClick={() => setSelectedRequest(null)}
                  className="text-gray-400 hover:text-gray-700 text-xl"
                >
                  ✕
                </button>

              </div>

              {/* INFO */}

              <div className="space-y-4 text-sm mb-6">

                <div className="flex justify-between">
                  <span className="text-gray-500">Barrio</span>
                  <span className="font-medium">
                    {selectedRequest.barrio}
                  </span>
                </div>

                <div className="flex justify-between">
                  <span className="text-gray-500">Área municipal</span>
                  <span className="font-medium">
                    {selectedRequest.area}
                  </span>
                </div>

                <div className="flex justify-between">
                  <span className="text-gray-500">Estado</span>
                  <StatusBadge estado={selectedRequest.estado} />
                </div>

                <div className="border-t pt-4">

                  <p className="text-gray-500 mb-1">
                    Descripción
                  </p>

                  <p className="text-gray-800 leading-relaxed">
                    {selectedRequest.descripcion}
                  </p>

                </div>

              </div>

              {/* TIMELINE */}

              <div className="border-t pt-6">

                <p className="text-sm font-semibold text-gray-700 mb-4">
                  Progreso de la solicitud
                </p>

                <div className="space-y-3">

                  {timelineStates.map((step, i) => {

                    const currentIndex =
                      getCurrentIndex(selectedRequest.estado);

                    const completed =
                      i <= currentIndex;

                    return (

                      <div
                        key={step.key}
                        className="flex items-center gap-3"
                      >

                        <div
                          className={`w-4 h-4 rounded-full ${
                            completed
                              ? "bg-green-500"
                              : "bg-gray-300"
                          }`}
                        />

                        <span
                          className={`text-sm ${
                            completed
                              ? "text-gray-900 font-medium"
                              : "text-gray-500"
                          }`}
                        >
                          {step.label}
                        </span>

                      </div>

                    );

                  })}

                </div>

              </div>

              {/* ACTION */}

              <div className="flex justify-end mt-8">

                <Button
                  variant="secondary"
                  onClick={() => setSelectedRequest(null)}
                >
                  Cerrar
                </Button>

              </div>

            </motion.div>

          </motion.div>

        )}

      </AnimatePresence>

    </Container>
  );
}